import { BookOpen, CalendarDays, Calendar as CalendarIcon, Coffee, UtensilsCrossed } from 'lucide-react';
import type { ReactNode } from 'react';

import { describeMinutes } from '../timetableWeek';

export interface TimetableSummaryCardsProps {
  /** Teaching slots across the whole week — periods × working days. */
  totalPeriods: number;
  /** Teaching rows in one day. */
  periodsPerDay: number;
  workingDaysCount: number;
  /** Distinct subjects actually placed in this section's week. */
  subjectCount: number;
  shortBreakCount: number;
  /** Total minutes of short break in a day, or 0 when there are none. */
  shortBreakMinutes: number;
  lunchBreakCount: number;
  lunchBreakMinutes: number;
}

/**
 * The week in five numbers.
 *
 * Every one of them is counted from the institute's own periods and entries.
 * They used to fall back to invented figures whenever the count came out zero —
 * "42 total periods", "1 short break (15 min)" — so an institute that had not
 * set its day up yet was shown somebody else's timetable in miniature. A zero
 * is now printed as a zero, which is the fact the office needs to see.
 */
export function TimetableSummaryCards({
  totalPeriods,
  periodsPerDay,
  workingDaysCount,
  subjectCount,
  shortBreakCount,
  shortBreakMinutes,
  lunchBreakCount,
  lunchBreakMinutes,
}: TimetableSummaryCardsProps) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5 sm:gap-4 print:hidden">
      <SummaryCard
        icon={<CalendarDays className="h-4 w-4 sm:h-5 sm:w-5" />}
        label="Total periods"
        value={String(totalPeriods)}
        note={`${periodsPerDay} a day`}
        tone="indigo"
      />
      <SummaryCard
        icon={<CalendarIcon className="h-4 w-4 sm:h-5 sm:w-5" />}
        label="School days"
        value={workingDaysCount === 1 ? '1 day' : `${workingDaysCount} days`}
        tone="blue"
      />
      <SummaryCard
        icon={<BookOpen className="h-4 w-4 sm:h-5 sm:w-5" />}
        label="Total subjects"
        value={String(subjectCount)}
        note="this section"
        tone="emerald"
      />
      <SummaryCard
        icon={<Coffee className="h-4 w-4 sm:h-5 sm:w-5" />}
        label="Short breaks"
        value={String(shortBreakCount)}
        note={shortBreakCount > 0 ? describeMinutes(shortBreakMinutes) : undefined}
        tone="orange"
      />
      <SummaryCard
        icon={<UtensilsCrossed className="h-4 w-4 sm:h-5 sm:w-5" />}
        label="Lunch breaks"
        value={String(lunchBreakCount)}
        note={lunchBreakCount > 0 ? describeMinutes(lunchBreakMinutes) : undefined}
        tone="rose"
        className="col-span-2 sm:col-span-1"
      />
    </div>
  );
}

/** The five cards differ only in colour and contents, so they are one component. */
const TONE = {
  indigo: {
    card: 'bg-[#f8f7ff] dark:bg-[#13162b] border-[#ece8ff] dark:border-[#222144] shadow-indigo-500/10 hover:shadow-indigo-500/20',
    dot: 'bg-gradient-to-br from-[#8b5cf6] to-[#6366f1] shadow-indigo-500/35',
  },
  blue: {
    card: 'bg-[#f4f8ff] dark:bg-[#0e1a2e] border-[#e0edff] dark:border-[#1c2e4f] shadow-blue-500/10 hover:shadow-blue-500/20',
    dot: 'bg-gradient-to-br from-[#3b82f6] to-[#60a5fa] shadow-blue-500/35',
  },
  emerald: {
    card: 'bg-[#f2faf5] dark:bg-[#0d2117] border-[#dcf3e4] dark:border-[#173e2a] shadow-emerald-500/10 hover:shadow-emerald-500/20',
    dot: 'bg-gradient-to-br from-[#10b981] to-[#34d399] shadow-emerald-500/35',
  },
  orange: {
    card: 'bg-[#fff8f2] dark:bg-[#26170c] border-[#ffe8d6] dark:border-[#472912] shadow-orange-500/10 hover:shadow-orange-500/20',
    dot: 'bg-gradient-to-br from-[#f97316] to-[#fb923c] shadow-orange-500/35',
  },
  rose: {
    card: 'bg-[#fff2f6] dark:bg-[#280e1b] border-[#ffdce6] dark:border-[#47152c] shadow-rose-500/10 hover:shadow-rose-500/20',
    dot: 'bg-gradient-to-br from-[#f43f5e] to-[#fb7185] shadow-rose-500/35',
  },
} as const;

interface SummaryCardProps {
  icon: ReactNode;
  label: string;
  value: string;
  note?: string;
  tone: keyof typeof TONE;
  className?: string;
}

function SummaryCard({ icon, label, value, note, tone, className }: SummaryCardProps) {
  const colours = TONE[tone];

  return (
    <div
      className={`rounded-2xl sm:rounded-3xl border p-3 sm:p-5 flex items-center gap-2.5 sm:gap-3.5 shadow-xs sm:shadow-md hover:shadow-xl hover:-translate-y-0.5 sm:hover:-translate-y-1 transition-all duration-250 ease-out ${colours.card} ${className ?? ''}`}
    >
      <div
        className={`h-8 w-8 sm:h-11 sm:w-11 rounded-full flex items-center justify-center text-white shadow-2xs sm:shadow-md shrink-0 ${colours.dot}`}
      >
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <span className="text-xs sm:text-[14px] text-slate-500 dark:text-slate-400 block font-bold leading-tight">
          {label}
        </span>
        <span className="font-black text-slate-800 dark:text-white text-2xl mt-0.5 block tracking-tight truncate">
          {value}
          {note && (
            <span className="text-[11px] sm:text-xs font-bold text-slate-500 dark:text-slate-400"> ({note})</span>
          )}
        </span>
      </div>
    </div>
  );
}
